import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookies } from 'react-cookie-consent';
import Modal from './modals/Modal';
import { useCookies } from '../context/CookieContext';

// Definiujemy typy dla preferencji plików cookie
type CookieCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

function CookiesManager() {
  const { isModalOpen, closeCookieModal } = useCookies();
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // zawsze true, nie można wyłączyć
    functional: false,
    analytics: false,
    marketing: false,
  });

  // Sprawdź, czy użytkownik już zaakceptował cookies
  useEffect(() => {
    const hasConsent = Cookies.get('cookieconsent_status');
    if (!hasConsent) {
      setShowBanner(true);
    } else {
      // Załaduj zapisane preferencje
      const functional = Cookies.get('cookieconsent_functional') === 'true';
      const analytics = Cookies.get('cookieconsent_analytics') === 'true';
      const marketing = Cookies.get('cookieconsent_marketing') === 'true';

      setPreferences({
        essential: true,
        functional,
        analytics,
        marketing,
      });
    }
  }, []);

  // Funkcja zapisująca preferencje
  const savePreferences = () => {
    // Zapisz każdą preferencję w osobnym cookie
    Cookies.set('cookieconsent_essential', 'true', { path: '/', expires: 365 }); // zawsze true
    Cookies.set('cookieconsent_functional', String(preferences.functional), {
      path: '/',
      expires: 365,
    });
    Cookies.set('cookieconsent_analytics', String(preferences.analytics), {
      path: '/',
      expires: 365,
    });
    Cookies.set('cookieconsent_marketing', String(preferences.marketing), {
      path: '/',
      expires: 365,
    });

    // Ustaw główne cookie zgody
    Cookies.set('cookieconsent_status', 'allow', { path: '/', expires: 365 });

    closeCookieModal();
    setShowBanner(false);
  };

  // Funkcja do obsługi zmiany preferencji
  const handleChange = (category: CookieCategory) => {
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Funkcja do akceptacji wszystkich
  const acceptAll = () => {
    // Najpierw aktualizujemy stan preferencji
    setPreferences({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    });

    // Bezpośrednio zapisujemy wartości zamiast polegać na zaktualizowanym stanie
    Cookies.set('cookieconsent_essential', 'true', { path: '/', expires: 365 });
    Cookies.set('cookieconsent_functional', 'true', { path: '/', expires: 365 });
    Cookies.set('cookieconsent_analytics', 'true', { path: '/', expires: 365 });
    Cookies.set('cookieconsent_marketing', 'true', { path: '/', expires: 365 });
    Cookies.set('cookieconsent_status', 'allow', { path: '/', expires: 365 });

    closeCookieModal();
    setShowBanner(false);
  };

  const openPreferences = () => {
    closeCookieModal(); // Zamyka modal, ale otwiera go przy następnym renderze
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          >
            <motion.div
              className="max-w-6xl mx-auto bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-2xl overflow-hidden"
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
                {/* Cookie Icon */}
                <motion.div
                  className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0"
                  whileHover={{ rotate: 15 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                >
                  <svg
                    className="w-6 h-6 text-blue-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10.5 1a.75.75 0 01.75.75c0 .414.336.75.75.75h3.5a.75.75 0 01.75.75v3.5a.75.75 0 00.75.75.75.75 0 01.75.75.75.75 0 01-.75.75.75.75 0 00-.75.75v3.5a.75.75 0 01-.75.75h-3.5a.75.75 0 00-.75.75.75.75 0 01-.75.75.75.75 0 01-.75-.75.75.75 0 00-.75-.75h-3.5a.75.75 0 01-.75-.75v-3.5a.75.75 0 00-.75-.75.75.75 0 01-.75-.75.75.75 0 01.75-.75.75.75 0 00.75-.75v-3.5a.75.75 0 01.75-.75h3.5a.75.75 0 00.75-.75.75.75 0 01.75-.75z" />
                  </svg>
                </motion.div>

                {/* Content */}
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white mb-2">
                    Twoja prywatność
                  </h2>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Używamy plików cookie, aby zapewnić poprawne działanie
                    strony, analizować ruch i dostosować treści do Twoich
                    preferencji. Pliki cookie są niezbędne do prawidłowego
                    działania witryny, ale niektóre mają charakter opcjonalny.
                  </p>

                  <div className="mt-3">
                    <a
                      href="/regulamin"
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center"
                    >
                      Polityka Prywatności
                      <svg
                        className="w-3 h-3 ml-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </a>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 mt-2 md:mt-0">
                  <motion.button
                    type="button"
                    onClick={openPreferences}
                    className="px-5 py-2 bg-transparent border border-gray-400 text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-700/30 transition-colors"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Dostosuj ustawienia
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={acceptAll}
                    className="px-5 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg text-sm font-medium shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transition-all"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Akceptuj wszystkie
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal do zarządzania preferencjami plików cookie */}
      <Modal
        show={isModalOpen}
        onClose={closeCookieModal}
        title="Ustawienia plików cookie"
        size="md"
      >
        <div className="p-4">
          <p className="mb-6 text-gray-600">
            Wybierz kategorie plików cookie, na które wyrażasz zgodę. Możesz w
            każdej chwili zmienić te ustawienia w stopce naszej strony.
          </p>

          {/* Kategorie plików cookie */}
          <div className="space-y-5 mb-8">
            {/* Essential cookies - zawsze włączone */}
            <div className="p-4 border border-gray-200 rounded-xl bg-gray-50 transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-800">Niezbędne</h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-2 ml-11">
                    Pliki niezbędne do funkcjonowania strony i usług. Nie mogą
                    być wyłączone.
                  </p>
                </div>
                <div className="w-12 h-6 bg-green-500 rounded-full relative">
                  <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow-sm"></span>
                </div>
              </div>
            </div>

            {/* Functional cookies */}
            <motion.div
              className="p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-all"
              whileHover={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <svg
                        className="w-4 h-4 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-800">
                      Funkcjonalne
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-2 ml-11">
                    Umożliwiają lepszą personalizację i zapamiętują Twoje
                    preferencje.
                  </p>
                </div>
                <motion.button
                  onClick={() => handleChange('functional')}
                  className={`w-12 h-6 ${preferences.functional ? 'bg-blue-500' : 'bg-gray-300'} rounded-full relative focus:outline-none`}
                  type="button"
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.span
                    className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
                    initial={false}
                    animate={{
                      left: preferences.functional
                        ? 'calc(100% - 20px)'
                        : '4px',
                    }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  ></motion.span>
                </motion.button>
              </div>
            </motion.div>

            {/* Analytics cookies */}
            <motion.div
              className="p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-all"
              whileHover={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                      <svg
                        className="w-4 h-4 text-purple-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                        <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-800">Analityczne</h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-2 ml-11">
                    Pomagają nam zrozumieć, jak odwiedzający wchodzą w
                    interakcję z witryną.
                  </p>
                </div>
                <motion.button
                  onClick={() => handleChange('analytics')}
                  className={`w-12 h-6 ${preferences.analytics ? 'bg-purple-500' : 'bg-gray-300'} rounded-full relative focus:outline-none`}
                  type="button"
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.span
                    className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
                    initial={false}
                    animate={{
                      left: preferences.analytics ? 'calc(100% - 20px)' : '4px',
                    }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  ></motion.span>
                </motion.button>
              </div>
            </motion.div>

            {/* Marketing cookies */}
            <motion.div
              className="p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-all"
              whileHover={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                      <svg
                        className="w-4 h-4 text-orange-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-gray-800">
                      Marketingowe
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-2 ml-11">
                    Używane do śledzenia i dostarczania spersonalizowanych
                    reklam.
                  </p>
                </div>
                <motion.button
                  onClick={() => handleChange('marketing')}
                  className={`w-12 h-6 ${preferences.marketing ? 'bg-orange-500' : 'bg-gray-300'} rounded-full relative focus:outline-none`}
                  type="button"
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.span
                    className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm"
                    initial={false}
                    animate={{
                      left: preferences.marketing ? 'calc(100% - 20px)' : '4px',
                    }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  ></motion.span>
                </motion.button>
              </div>
            </motion.div>
          </div>

          {/* Przyciski akcji */}
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <motion.button
              onClick={acceptAll}
              className="px-5 py-2.5 bg-gray-100 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Akceptuj wszystkie
            </motion.button>
            <motion.button
              onClick={savePreferences}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg text-sm font-medium shadow-md hover:shadow-lg transition-shadow"
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Zapisz preferencje
            </motion.button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default CookiesManager;
